/**
 * SenderAddressSettings - Sender Address Configuration Component
 * 
 * Allows users to configure their return/sender address for mailed letters.
 * Used in the Settings page.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  MapPin,
  Save,
  Loader2,
  CheckCircle,
  AlertCircle,
  Mail,
  Building2,
  User,
  Home,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const SenderAddressSettings = () => {
  const { user, token, getAuthHeaders } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [address, setAddress] = useState({
    name: '',
    company_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US'
  });
  const [isComplete, setIsComplete] = useState(false);
  const [missingFields, setMissingFields] = useState([]);

  // Load current address
  useEffect(() => {
    const loadAddress = async () => {
      try {
        const response = await axios.get(
          `${API}/mailing-config/sender-address`,
          { headers: getAuthHeaders(), withCredentials: true }
        );
        
        if (response.data.success && response.data.address) {
          setAddress(response.data.address);
          setIsComplete(response.data.is_complete);
        }
      } catch (error) {
        console.error('Failed to load sender address:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadAddress();
  }, [getAuthHeaders]);

  // Validate address
  useEffect(() => {
    const required = ['name', 'address_line1', 'city', 'state', 'postal_code'];
    const missing = required.filter(field => !address[field]);
    setMissingFields(missing);
    setIsComplete(missing.length === 0);
  }, [address]);

  const handleChange = (field, value) => {
    setAddress(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/mailing-config/sender-address`,
        { address },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      if (response.data.success) {
        setIsComplete(response.data.is_complete);
        setMissingFields(response.data.missing_fields || []);
        toast.success('Sender address saved successfully');
      }
    } catch (error) {
      console.error('Failed to save sender address:', error);
      toast.error('Failed to save sender address');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="animate-spin text-slate-400" size={24} />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="sender-address-settings">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
            <MapPin size={20} className="text-emerald-600" />
          </div>
          <div>
            <h3 className="font-semibold text-stone-900">Sender Address</h3>
            <p className="text-sm text-slate-600">
              Your return address for mailed dispute letters
            </p>
          </div>
        </div>
        
        {isComplete ? (
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
            <CheckCircle size={14} className="mr-1" />
            Complete
          </Badge>
        ) : (
          <Badge className="bg-amber-100 text-amber-700 border-amber-200">
            <AlertCircle size={14} className="mr-1" />
            Incomplete
          </Badge>
        )}
      </div>

      {/* Mailing Readiness Note */}
      {!isComplete && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Mail size={18} className="text-amber-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-900 mb-1">Address Required for Mailing</h4>
              <p className="text-sm text-amber-800">
                Please complete your sender address to enable postal mailing of dispute letters.
                Missing: {missingFields.join(', ')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Address Form */}
      <div className="space-y-4">
        {/* Name */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="sender-name" className="flex items-center gap-2 mb-1.5">
              <User size={14} className="text-slate-400" />
              Full Name *
            </Label>
            <Input
              id="sender-name"
              value={address.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="John Smith"
              className={!address.name ? 'border-amber-300' : ''}
              data-testid="sender-name-input"
            />
          </div>
          
          <div>
            <Label htmlFor="sender-company" className="flex items-center gap-2 mb-1.5">
              <Building2 size={14} className="text-slate-400" />
              Company Name (Optional)
            </Label>
            <Input
              id="sender-company"
              value={address.company_name}
              onChange={(e) => handleChange('company_name', e.target.value)}
              placeholder="Company Name"
              data-testid="sender-company-input"
            />
          </div>
        </div>

        {/* Address Lines */}
        <div>
          <Label htmlFor="sender-address1" className="flex items-center gap-2 mb-1.5">
            <Home size={14} className="text-slate-400" />
            Street Address *
          </Label>
          <Input
            id="sender-address1"
            value={address.address_line1}
            onChange={(e) => handleChange('address_line1', e.target.value)}
            placeholder="123 Main Street"
            className={!address.address_line1 ? 'border-amber-300' : ''}
            data-testid="sender-address1-input"
          />
        </div>

        <div>
          <Label htmlFor="sender-address2" className="mb-1.5">
            Address Line 2 (Optional)
          </Label>
          <Input
            id="sender-address2"
            value={address.address_line2}
            onChange={(e) => handleChange('address_line2', e.target.value)}
            placeholder="Apt, Suite, Unit, etc."
            data-testid="sender-address2-input"
          />
        </div>

        {/* City, State, Zip */}
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="sender-city" className="mb-1.5">City *</Label>
            <Input
              id="sender-city"
              value={address.city}
              onChange={(e) => handleChange('city', e.target.value)}
              placeholder="City"
              className={!address.city ? 'border-amber-300' : ''}
              data-testid="sender-city-input"
            />
          </div>
          
          <div>
            <Label htmlFor="sender-state" className="mb-1.5">State *</Label>
            <Input
              id="sender-state"
              value={address.state}
              onChange={(e) => handleChange('state', e.target.value.toUpperCase().slice(0, 2))}
              placeholder="GA"
              maxLength={2}
              className={!address.state ? 'border-amber-300' : ''}
              data-testid="sender-state-input"
            />
          </div>
          
          <div>
            <Label htmlFor="sender-zip" className="mb-1.5">ZIP Code *</Label>
            <Input
              id="sender-zip"
              value={address.postal_code}
              onChange={(e) => handleChange('postal_code', e.target.value.replace(/[^\d-]/g, '').slice(0, 10))}
              placeholder="30301"
              className={!address.postal_code ? 'border-amber-300' : ''}
              data-testid="sender-zip-input"
            />
          </div>
        </div>

        {/* Country (read-only for now) */}
        <div className="w-48">
          <Label htmlFor="sender-country" className="mb-1.5">Country</Label>
          <Input
            id="sender-country"
            value="United States"
            disabled
            className="bg-slate-50"
            data-testid="sender-country-input"
          />
        </div>
      </div>

      {/* Save Button */}
      <div className="flex items-center justify-between mt-6 pt-6 border-t border-slate-200">
        <p className="text-sm text-slate-500">
          * Required fields for postal mailing
        </p>
        
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="save-sender-address-btn"
        >
          {saving ? (
            <Loader2 size={16} className="mr-2 animate-spin" />
          ) : (
            <Save size={16} className="mr-2" />
          )}
          Save Address
        </Button>
      </div>
    </div>
  );
};

export default SenderAddressSettings;
